import * as z from 'zod';
import { parseAt } from './boundary';
import { commandSchema } from './commands';
import { mapDocumentSchema } from './document';
import { mapIdSchema, operationIdSchema } from './ids';
import { logicalClockSchema, timestampSchema } from './primitives';
/**
 * The most commands one push may carry.
 *
 * Matched by the database's lookup cap, and both are there for the same reason: a push is a
 * batch of work, not a session's worth of it. A client with more than this in its outbox sends
 * several pushes, each of which is a revision, which is exactly what a long time offline should
 * produce — a history, rather than one enormous jump nobody can restore to a point inside.
 */
export const MAX_PUSH_COMMANDS = 500;
/** Registering a map the server has never seen. */
export const syncRegisterRequestSchema = z
    .object({
    /*
     * `unknown` on purpose. The document is checked by `parseDocument`, which does what a schema
     * cannot: it walks the tree afterwards and refuses one whose parents, ordering or text do not
     * hang together. Validating it twice — once loosely here and once properly there — would
     * report the shallow failure first and hide the real one.
     */
    document: z.unknown(),
    operationIds: z.array(operationIdSchema).max(100_000).optional(),
})
    .strict();
export const syncPushRequestSchema = z
    .object({
    /** The revision these commands were made against. 0 is a client that has never synced. */
    baseRevision: logicalClockSchema,
    commands: z.array(commandSchema).min(1).max(MAX_PUSH_COMMANDS),
})
    .strict();
/** What every answer carries: where the server is now. */
export const syncStateSchema = z
    .object({
    mapId: mapIdSchema,
    revision: logicalClockSchema,
    updatedAt: timestampSchema,
})
    .strict();
/**
 * `GET /sync/maps`: which maps the account has on the server, and at which revision.
 *
 * What a second device needs to learn that a map exists at all. A map is otherwise only ever
 * synchronised by the device that opened it, so one made on a phone would never reach a laptop
 * that has not heard of it. Metadata only: the documents are pulled one by one, and only the ones
 * a device is missing or behind on.
 */
export const syncListResponseSchema = z
    .object({
    maps: z.array(syncStateSchema
        .extend({ title: z.string(), deletedAt: timestampSchema.nullable().optional() })
        .strict()),
})
    .strict();
export const syncPullResponseSchema = syncStateSchema
    .extend({
    document: mapDocumentSchema,
})
    .strict();
/**
 * What a push did.
 *
 * A discriminated union rather than a status code, because "you are behind" is not an error: it
 * is an ordinary answer to an ordinary question, it arrives with the information needed to act on
 * it, and a client that treated it as a failure would retry the same doomed push instead of
 * pulling. The HTTP status for both is 200; the failures that *are* failures — not signed in, not
 * yours, not a map — never reach this shape.
 */
export const syncPushResponseSchema = z.discriminatedUnion('status', [
    z
        .object({
        status: z.literal('applied'),
        mapId: mapIdSchema,
        revision: logicalClockSchema,
        updatedAt: timestampSchema,
        /**
         * Every operation this push is now known to hold — the ones just applied *and* the ones the
         * server already had. A retry after a lost acknowledgement is told about both, because from
         * the client's side they are the same thing: work it can stop sending.
         */
        acknowledged: z.array(operationIdSchema),
    })
        .strict(),
    z
        .object({
        status: z.literal('behind'),
        mapId: mapIdSchema,
        /** Where the server actually is. The client pulls this, rebases, and pushes again. */
        revision: logicalClockSchema,
        updatedAt: timestampSchema,
    })
        .strict(),
]);
export function parsePushRequest(value, boundary = 'network', context = 'sync push') {
    const parsed = parseAt(syncPushRequestSchema, value, boundary, context);
    if (!parsed.ok)
        return parsed;
    return {
        ok: true,
        value: {
            baseRevision: parsed.value.baseRevision,
            // `parseAt` has established every field; what differs between the two descriptions is how
            // a branded string is spelled, which `__tests__/command-drift.test-d.ts` is what keeps in
            // step.
            commands: parsed.value.commands,
        },
    };
}
// ---------------------------------------------------------------------------------------------
// history (step 32)
// ---------------------------------------------------------------------------------------------
/**
 * One version, as a history panel reads it.
 *
 * The storage key is deliberately absent. It names a workspace and a map, it is the thing a
 * signed grant is minted for, and a client that wanted the bytes asks for them by revision rather
 * than by address — which keeps the layout of the bucket a server-side fact.
 */
export const syncVersionSchema = z
    .object({
    id: z.string().min(1),
    revision: logicalClockSchema,
    sizeBytes: z.number().int().nonnegative(),
    kind: z.enum(['checkpoint', 'manual', 'restore']),
    label: z.string().nullable(),
    createdAt: timestampSchema,
})
    .strict();
export const syncHistoryResponseSchema = z
    .object({
    mapId: mapIdSchema,
    revision: logicalClockSchema,
    versions: z.array(syncVersionSchema),
})
    .strict();
/**
 * Restoring is *forward*, never a rewind.
 *
 * The request names the revision to bring back, and what the server does is write its content as
 * a new revision on top. History stays append-only: the versions between are still there, still
 * restorable, and "restore to yesterday" cannot destroy today by being clicked twice. It is also
 * the only shape that works with the concurrency rule — a rewind would have to un-advance a
 * revision, and `advanceRevision` refuses to go backwards for good reasons.
 */
export const syncRestoreRequestSchema = z.object({ revision: logicalClockSchema }).strict();
//# sourceMappingURL=sync.js.map